import { type ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type {
  DeleteDiscountMutationInput, Mutation, Query, SaveDiscountMutationInput,
} from '../../../generated/api.graphql';
import * as VPErrors from '../../../VPError';
import type { VPResult } from '../../VPResult';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import * as Discount from './Discount';
import * as DiscountInput from './DiscountInput';
import { DISCOUNT_FIELDS } from './fragments';

/**
 * Discount administration on the tenant (AppSchema) connection, for the Discounts tab. Auth is
 * `view_tickets?` to read and `edit_tickets?` to write, server-side. **Nothing here catches**, as
 * in `App/TeamRole`: a transport failure propagates, and `success: false` covers only the
 * GraphQL-level outcomes.
 */

const failure = (message: string): VPResult<never, VPErrors.VPError[]> => ({
  success: false,
  errors: [{ path: '', message }],
});

export const DISCOUNTS_QUERY = gql`
  query Discounts($eventId: Int!) {
    discounts(eventId: $eventId) {
      ...DiscountFields
    }
  }
  ${DISCOUNT_FIELDS}
`;

export const SAVE_DISCOUNT_MUTATION = gql`
  mutation saveDiscount($input: SaveDiscountMutationInput!) {
    saveDiscount(input: $input) {
      discount {
        ...DiscountFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${DISCOUNT_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const DELETE_DISCOUNT_MUTATION = gql`
  mutation deleteDiscount($input: DeleteDiscountMutationInput!) {
    deleteDiscount(input: $input) {
      success
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

/**
 * The discounts of one event: a plain list, no paginator of either kind, `network-only` as
 * `fetchVenues` is. It is *nullable*, so a null resolves `success: false` rather than flattening
 * to `[]`, which would render "no discounts" over a failed read — the rule
 * `doc/claude/api-layer.md` states, and which `fetchTicketTypes` predates by throwing.
 */
export const fetchDiscounts = async (
  client: ApolloClient<NormalizedCacheObject>,
  eventId: number,
): Promise<VPResult<Discount.Discount[], VPErrors.VPError[]>> => {
  const { data } = await client.query<Query>({
    query: DISCOUNTS_QUERY,
    fetchPolicy: 'network-only',
    variables: { eventId },
  });

  if (!data?.discounts) {
    return failure('Failed to fetch the discounts.');
  }

  return { success: true, data: data.discounts.map(Discount.fromGql) };
};

/** Creates when the input carries no `id`, updates when it does. */
export const saveDiscount = async (
  client: ApolloClient<NormalizedCacheObject>,
  discount: DiscountInput.DiscountInput,
): Promise<VPResult<Discount.Discount, VPErrors.VPError[]>> => {
  // Typed rather than inlined: the `mutate<Mutation>` generic does not check `variables`.
  const input: SaveDiscountMutationInput = { discount: DiscountInput.toGqlInput(discount) };

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_DISCOUNT_MUTATION,
    variables: { input },
  });

  const payload = data?.saveDiscount;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  // No `success` here, so an empty error list with no entity is the one shape that would
  // otherwise read as a silent success — `saveVenue`'s derivation.
  if (!payload?.discount) {
    return failure('Failed to save the discount.');
  }

  return { success: true, data: Discount.fromGql(payload.discount) };
};

export const deleteDiscount = async (
  client: ApolloClient<NormalizedCacheObject>,
  discountId: number,
): Promise<VPResult<null, VPErrors.VPError[]>> => {
  // An `Int!`, so no `ID!`-to-string coercion on the way out, unlike `deleteVenue`.
  const input: DeleteDiscountMutationInput = { discountId };

  const { data } = await client.mutate<Mutation>({
    mutation: DELETE_DISCOUNT_MUTATION,
    variables: { input },
  });

  const payload = data?.deleteDiscount;

  // This payload does carry `success`, so the outcome comes off the flag, not the error list.
  if (payload?.success) {
    return { success: true, data: null };
  }

  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  // A failure the caller cannot render is worse than a generic message.
  return errors.length ? { success: false, errors } : failure('Failed to delete the discount.');
};
